// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, FormGroup, InputLabel, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { deleteMaterial, getMaterialList } from '~/thunks/material/materialThunk';
import { selectIsRefreshMaterialList, materialActions } from '~/thunks/material/materialSlice';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import { IBaseOption, IFilterValueChange, IHeaderCellType } from '~/utils/interface/common';
import { IMaterial, IQueryParamMaterial } from '~/utils/interface/material';
import { formatCurrency, formatDecimal, formatTitleCase } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { StorageEnum } from '~/utils/enum/base';
import useDebounce from '~/utils/hooks/useDebounce';
import { IPaginationResponse } from '~/utils/interface/base';
import { RoleEnum, SearchByFilterContentEnum, SearchByFilterEnum } from '~/utils/enum/common';
import { mockDataMaterialUnit } from '~/utils/mockData/material';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './Material.module.scss';
import { icons, images } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<IMaterial>[] => {
  return [
    {
      id: 'name',
      label: t('table_material_title_name'),
      render: (record: IMaterial) => {
        return (
          <Box component={'div'} className={cx('viewName')}>
            {record?.materialImage ? (
              <img src={record?.materialImage} alt={t('common_alt_img')} className={cx('image')} />
            ) : (
              <img src={images.imgDefaultInventory} alt={t('common_alt_img')} className={cx('image')} />
            )}

            {record?.name || EMPTY_STRING}
          </Box>
        );
      },
      minWidth: 150,
    },
    {
      id: 'partNumber',
      label: t('table_material_title_part_number'),
      render: (record: IMaterial) => record?.partNumber || EMPTY_STRING,
    },
    {
      id: 'cost',
      label: t('table_material_title_cost'),
      render: (record: IMaterial) => record?.cost || EMPTY_STRING,
    },
    {
      id: 'price',
      label: t('table_material_title_price'),
      render: (record: IMaterial) => formatCurrency(Number(record?.price)) || EMPTY_STRING,
    },
    {
      id: 'unit',
      label: t('table_material_title_unit'),
      render: (record: IMaterial) => {
        const unit: IBaseOption = mockDataMaterialUnit.find(
          (item) => item.value === record?.unit
        ) as IBaseOption;

        return unit ? unit?.label : EMPTY_STRING;
      },
    },
    {
      id: 'quantity',
      label: t('table_material_title_quantity'),
      render: (record: IMaterial) => (
        <Box
          component={'div'}
          className={cx('quantity', { negative: Number(record?.quantity) < DEFAULT_NUMBER_ZERO })}
        >
          {record?.quantity ? formatDecimal(Number(record?.quantity)) : DEFAULT_NUMBER_ZERO}
        </Box>
      ),
    },
    {
      id: 'action',
      label: t('table_material_title_action'),
      align: 'left',
      render: (record: IMaterial) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const Material = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/material']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshMaterialList = useSelector(selectIsRefreshMaterialList);
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryParamMaterial>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamMaterial>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [totalItems, setTotalItems] = useState<string>();
  const [materialList, setMaterialList] = useState<IMaterial[]>([]);

  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [materialSelected, setMaterialSelected] = useState<string | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetMaterialList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshMaterialList) return;

    handleGetMaterialList(paramObject);
    dispatch(materialActions.setRefreshMaterialList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshMaterialList]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetMaterialList = (params: IQueryParamMaterial) => {
    setIsLoading(true);

    dispatch(getMaterialList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setMaterialList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddMaterial = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createMaterial);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createMaterials);
        break;
    }
  };

  const handleEditMaterial = (materialId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.updateMaterial(materialId)}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.updateMaterial(materialId)}`);
        break;
    }
  };

  const handleViewMaterial = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.materials}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.materials}/${id}`);
        break;
    }
  };

  const handleDeleteMaterial = (materialId: string) => {
    if (!materialId) return;

    setMaterialSelected(materialId);
    setIsOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!materialSelected) return;

    setIsLoading(true);

    dispatch(deleteMaterial(materialSelected))
      .unwrap()
      .then(() => {
        dispatch(materialActions.setRefreshMaterialList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmDelete();
      });
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
    setMaterialSelected(null);
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleApplyFilter = (values: IQueryParamMaterial | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    setParamObject({
      ...paramObject,
      ...values,
    });
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryParamMaterial | undefined,
    onChange?: (value: IFilterValueChange<IQueryParamMaterial>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamMaterial = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }
  };

  const handleFilterUnitChange = (
    option: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamMaterial>) => void
  ) => {
    if (!option) return;

    const { value } = option;

    onChange?.({ name: 'unit', value: value });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_materials', { ns: 'common/sidebar' })}>
    <Box component={'div'} id='materialPageAdmin' className={cx('container')}>
      <BaseToolbar
        title={t('material_title_total', { quantity: totalItems })}
        textAction={t('button_add_material')}
        onAction={handleAddMaterial}
      >
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryParamMaterial>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.PART_NUMBER}
                      checked={value?.fields?.includes(SearchByFilterEnum.PART_NUMBER) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_part_number')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_unit')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'160px'}
                  options={mockDataMaterialUnit}
                  value={value?.unit}
                  onChange={(option) => handleFilterUnitChange(option, onChange)}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleViewMaterial, handleEditMaterial, handleDeleteMaterial)}
          dataSource={materialList || []}
          onPageChange={handlePageChange}
          onRowClick={handleViewMaterial}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isOpenConfirm && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
    </PageShell>
  );
};

export default Material;
